import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface AppChromeContextValue {
  /** Number of mounted surfaces requesting mobile chrome to be hidden. */
  hiddenOnMobileCount: number;
  /** Register one active request to hide mobile app chrome. */
  incrementHide: () => void;
  /** Clear one active request to hide mobile app chrome. */
  decrementHide: () => void;
}

const AppChromeContext = createContext<AppChromeContextValue | null>(null);

/**
 * Provides route and surface chrome state for the legacy SPA shell.
 *
 * The counter-based API lets multiple fullscreen surfaces compose safely:
 * mobile app chrome only returns once every requester has unmounted.
 */
export const AppChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hiddenOnMobileCount, setHiddenOnMobileCount] = useState(0);

  const incrementHide = useCallback((): void => {
    setHiddenOnMobileCount((count) => count + 1);
  }, []);

  const decrementHide = useCallback((): void => {
    setHiddenOnMobileCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo<AppChromeContextValue>(
    () => ({
      hiddenOnMobileCount,
      incrementHide,
      decrementHide,
    }),
    [decrementHide, hiddenOnMobileCount, incrementHide]
  );

  return (
    <AppChromeContext.Provider value={value}>
      {children}
    </AppChromeContext.Provider>
  );
};

const useAppChromeInternal = (): AppChromeContextValue => {
  const context = useContext(AppChromeContext);
  if (!context) {
    throw new Error("useAppChrome must be used within an AppChromeProvider");
  }
  return context;
};

/**
 * Reads the current app chrome state.
 */
export const useAppChrome = (): Pick<AppChromeContextValue, "hiddenOnMobileCount"> => {
  const { hiddenOnMobileCount } = useAppChromeInternal();
  return { hiddenOnMobileCount };
};

/**
 * Requests the persistent AppBar be hidden on mobile for this component's
 * lifetime. Desktop chrome remains visible; the app shell applies that policy.
 */
export const useHideAppBarOnMobile = (enabled: boolean = true): void => {
  const { incrementHide, decrementHide } = useAppChromeInternal();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    incrementHide();
    return () => {
      decrementHide();
    };
  }, [decrementHide, enabled, incrementHide]);
};

